/* global axios */
import ApiClient from './ApiClient';
import store from '../store';

class InternalChatsAPI extends ApiClient {
  constructor() {
    super('internal_chats', { accountScoped: true });
  }

  // eslint-disable-next-line class-methods-use-this
  get accountId() {
    return store.getters.getCurrentAccountId;
  }

  async list() {
    return axios.get(this.url);
  }

  async show(id) {
    return axios.get(`${this.url}/${id}`);
  }

  async createDm(userId) {
    return axios.post(`${this.url}/dm`, { user_id: userId });
  }

  async createGroup({ name, description, memberIds, avatar }) {
    if (avatar) {
      const fd = new FormData();
      fd.append('name', name);
      if (description) fd.append('description', description);
      memberIds.forEach(id => fd.append('member_ids[]', id));
      fd.append('avatar', avatar);
      return axios.post(`${this.url}/groups`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return axios.post(`${this.url}/groups`, {
      name,
      description,
      member_ids: memberIds,
    });
  }

  // Item 16 da fila: canal com permissão por departamento. teamId nulo =
  // canal aberto (todo mundo da conta vira participante, igual #geral).
  async createChannel({ name, description, teamId }) {
    return axios.post(`${this.url}/channels`, {
      name,
      description,
      team_id: teamId || undefined,
    });
  }

  async update(id, payload) {
    if (payload.avatar instanceof File) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v !== undefined && v !== null) fd.append(k, v);
      });
      return axios.patch(`${this.url}/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return axios.patch(`${this.url}/${id}`, payload);
  }

  async destroy(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  async addMembers(id, userIds) {
    return axios.post(`${this.url}/${id}/add_members`, { user_ids: userIds });
  }

  async removeMember(id, userId) {
    return axios.delete(`${this.url}/${id}/members/${userId}`);
  }

  async markRead(id) {
    return axios.post(`${this.url}/${id}/mark_read`);
  }

  // Item 9 da fila: silenciar conversa. duration: 'off' | '8h' | '1w' | 'always'.
  async toggleMute(id, duration) {
    return axios.post(`${this.url}/${id}/toggle_mute`, { duration });
  }

  async agents(query = '') {
    return axios.get(`${this.url}/agents`, { params: { q: query } });
  }

  async listMessages(chatId, { offset = 0, limit = 50 } = {}) {
    return axios.get(
      `/api/v1/accounts/${this.accountId}/internal_chats/${chatId}/messages`,
      { params: { offset, limit } }
    );
  }

  // Lê a discussão pendurada numa mensagem. Devolve `{ root, replies }` — a
  // raiz vem junto para o painel não piscar esperando uma segunda chamada.
  async listThread(chatId, messageId) {
    return axios.get(
      `/api/v1/accounts/${this.accountId}/internal_chats/${chatId}/messages/${messageId}/thread`
    );
  }

  // Liga/desliga o acompanhamento da thread. Devolve o estado resultante — o
  // inicial depende de dados (respondeu? abriu?), não de um booleano guardado,
  // então a tela não teria como calcular sozinha.
  async toggleThreadSubscription(chatId, messageId) {
    return axios.post(
      `/api/v1/accounts/${this.accountId}/internal_chats/${chatId}/messages/${messageId}/toggle_thread_subscription`
    );
  }

  // `threadParentId` entra no MESMO endpoint de criar mensagem: o backend
  // reaproveita o `create`, então anexo, menção e broadcast seguem um caminho
  // só. Duplicar aqui garantiria divergência no primeiro ajuste.
  async createMessage(
    chatId,
    {
      content,
      attachments = [],
      inReplyToId = null,
      threadParentId = null,
      alsoSendToChannel = false,
      // ISO-8601 com fuso. Quem valida e decide é o servidor — o relógio do
      // notebook de quem digitou não pode mandar na hora da entrega.
      scheduledAt = null,
    }
  ) {
    if (attachments.length > 0) {
      const fd = new FormData();
      if (content) fd.append('content', content);
      if (inReplyToId) fd.append('in_reply_to_id', inReplyToId);
      if (threadParentId) fd.append('thread_parent_id', threadParentId);
      if (alsoSendToChannel) fd.append('also_send_to_channel', '1');
      if (scheduledAt) fd.append('scheduled_at', scheduledAt);
      attachments.forEach(file => fd.append('attachments[]', file));
      return axios.post(
        `/api/v1/accounts/${this.accountId}/internal_chats/${chatId}/messages`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    }
    return axios.post(
      `/api/v1/accounts/${this.accountId}/internal_chats/${chatId}/messages`,
      {
        content,
        in_reply_to_id: inReplyToId,
        thread_parent_id: threadParentId,
        also_send_to_channel: alsoSendToChannel,
        scheduled_at: scheduledAt,
      }
    );
  }

  async updateMessage(chatId, messageId, content) {
    return axios.patch(
      `/api/v1/accounts/${this.accountId}/internal_chats/${chatId}/messages/${messageId}`,
      { content }
    );
  }

  async deleteMessage(chatId, messageId) {
    return axios.delete(
      `/api/v1/accounts/${this.accountId}/internal_chats/${chatId}/messages/${messageId}`
    );
  }

  async toggleReaction(chatId, messageId, emoji) {
    return axios.post(
      `/api/v1/accounts/${this.accountId}/internal_chats/${chatId}/messages/${messageId}/toggle_reaction`,
      { emoji }
    );
  }

  async togglePin(chatId, messageId) {
    return axios.post(
      `/api/v1/accounts/${this.accountId}/internal_chats/${chatId}/messages/${messageId}/toggle_pin`
    );
  }
}

export default new InternalChatsAPI();
